import { useEffect, useRef, type RefObject } from 'react';
import { createStore } from './store';

// Keyboard plumbing shared by the whole app.
// - Escape closes the top-most thing first: popovers, then dialogs, the composer, the palette, the notifications
//   panel, the open sheet, and finally whatever the current view offers (such as clearing a selection).
// - Views register the keys they handle (J, K, X, Enter) while they are on screen.
// - Compose contexts decide what E and C write: a reply in the Inbox, an email to the open person, and so on.

export const ESC = {
  popover: 100,
  menu: 95,
  dialog: 80,
  composer: 70,
  palette: 60,
  notifications: 50,
  admin: 40,
  record: 30,
  view: 10,
} as const;

interface Entry<T> {
  priority: number;
  order: number;
  fn: T;
}

let order = 0;

function stack<T>() {
  const entries: Entry<T>[] = [];
  return {
    push(priority: number, fn: T): () => void {
      const entry = { priority, order: order++, fn };
      entries.push(entry);
      return () => {
        const i = entries.indexOf(entry);
        if (i >= 0) entries.splice(i, 1);
      };
    },
    /** Entries from the top-most down. */
    sorted(): T[] {
      return [...entries].sort((a, b) => b.priority - a.priority || b.order - a.order).map((e) => e.fn);
    },
  };
}

const escapes = stack<() => boolean | void>();

/** Runs the top-most Escape handler. Returns true when something closed. */
export function runEscape(): boolean {
  for (const fn of escapes.sorted()) if (fn() !== false) return true;
  return false;
}

/**
 * Registers an Escape handler while `active` is true. Return false from the handler to let Escape fall through
 * to the next layer down.
 */
export function useEscape(active: boolean, priority: number, handler: () => boolean | void): void {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    if (!active) return;
    return escapes.push(priority, () => ref.current());
  }, [active, priority]);
}

export type ViewKeyHandler = (key: string, e: KeyboardEvent) => boolean;
const viewKeys = stack<ViewKeyHandler>();

/** Lets the current view handle J, K, X and Enter (passed in lower case, Enter as "enter"). Return true when handled. */
export function useViewKeys(handler: ViewKeyHandler, active = true): void {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    if (!active) return;
    return viewKeys.push(0, (k, e) => ref.current(k, e));
  }, [active]);
}

export function runViewKey(key: string, e: KeyboardEvent): boolean {
  for (const fn of viewKeys.sorted()) if (fn(key, e)) return true;
  return false;
}

export const COMPOSE = { view: 10, record: 30 } as const;
const composers = stack<() => void>();

/** Registers what E and C write while a view or record is showing (a reply in the Inbox, an email to this person). */
export function useComposeContext(active: boolean, priority: number, fn: () => void): void {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    if (!active) return;
    return composers.push(priority, () => ref.current());
  }, [active, priority]);
}

/** Runs the top-most compose context. Returns false when none is registered. */
export function runComposeContext(): boolean {
  const top = composers.sorted()[0];
  if (!top) return false;
  top();
  return true;
}

export function isTyping(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement) || el === document.body) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

/** "G then a letter" state, shown as the hint strip at the bottom of the screen. */
export const gStore = createStore<{ pending: boolean; hint: boolean }>({ pending: false, hint: false });

export const isMac = /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

// Focus traps: while a dialog, the composer or a sheet is open, Tab cycles inside the top-most one.
export const TRAP = { dialog: 80, composer: 70, palette: 60, admin: 40, record: 30 } as const;
const traps = stack<() => HTMLElement | null>();

/** Keeps Tab inside `ref` while `active` is true. The highest priority trap wins. */
export function useFocusTrap(ref: RefObject<HTMLElement | null>, active: boolean, priority: number): void {
  useEffect(() => {
    if (!active) return;
    return traps.push(priority, () => ref.current);
  }, [active, priority, ref]);
}

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"], [contenteditable="true"]';

/** Elements Tab can reach inside a container, in order, skipping hidden ones. */
export function focusables(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (el) => el.tabIndex !== -1 && (el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0),
  );
}

/** Handles Tab for the top-most trap. Returns true when it moved focus. */
export function trapTab(e: KeyboardEvent): boolean {
  const root = traps.sorted().map((get) => get()).find((el): el is HTMLElement => !!el && el.isConnected);
  if (!root) return false;
  const items = focusables(root);
  if (items.length === 0) {
    e.preventDefault();
    root.focus();
    return true;
  }
  const first = items[0]!;
  const last = items[items.length - 1]!;
  const active = document.activeElement as HTMLElement | null;
  if (!active || !root.contains(active)) {
    e.preventDefault();
    (e.shiftKey ? last : first).focus();
    return true;
  }
  if (e.shiftKey && (active === first || active === root)) {
    e.preventDefault();
    last.focus();
    return true;
  }
  if (!e.shiftKey && active === last) {
    e.preventDefault();
    first.focus();
    return true;
  }
  return false;
}
